// Client/src/components/NotificationSettings.tsx
/*
 * UPDATED: Benachrichtigungseinstellungen sind jetzt filial-bezogen
 * 
 * Neue Features:
 * - Zeitraum wird automatisch von Filial-Öffnungszeiten übernommen
 * - Option zur manuellen Zeitraumeinstellung weiterhin verfügbar
 * - Rollenbasierte Anzeige von Benachrichtigungstypen
 * - Integration mit openingHoursStore für Filial-Öffnungszeiten
 * - Automatische Berücksichtigung aller Öffnungstage (inkl. Samstag)
 * - Wochenend-Setting entfernt da Märkte auch samstags geöffnet sind
 * 
 * Logik:
 * - Bei Store-Zeiten: Benachrichtigungen nur während tatsächlicher Öffnungszeiten
 * - Bei manuellen Zeiten: Feste Uhrzeiten, unabhängig von Filial-Status
 * - Notfall-Benachrichtigungen sind immer erlaubt
 * 
 * Benutzerrollen und ihre relevanten Benachrichtigungen:
 * - Admin: Alle Benachrichtigungen
 * - Filialleiter: Task- und Reinigungs-Management, System-Notfälle  
 * - Reinigungskraft: Nur Reinigungsaufgaben und freigegebene Tasks
 * - User: Grundlegende Task-Benachrichtigungen
 */

import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Settings, TestTube, Monitor, Smartphone, Clock, AlertTriangle, Store, Trash2 } from 'lucide-react';
import { NotificationService } from '../services/notificationService';
import type { NotificationSettings as INotificationSettings, PushSubscriptionInfo } from '../services/notificationService';
import { useAuthStore } from '../store/authStore';
import { useOpeningHoursStore, type OpeningHoursResponse } from '../store/openingHoursStore';
import { getPushEnvironment, getPushSupportMessage } from '../utils/pushEnvironment';
import Portal from './Portal';

interface NotificationSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({ isOpen, onClose }) => {
  const [settings, setSettings] = useState<INotificationSettings | null>(null);
  const [subscriptions, setSubscriptions] = useState<PushSubscriptionInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission>('default');

  // Store-bezogene States
  const { user, selectedStoreId, stores } = useAuthStore();
  const { getOpeningHours } = useOpeningHoursStore();
  const [storeOpeningHours, setStoreOpeningHours] = useState<OpeningHoursResponse | null>(null);
  const [useStoreHours, setUseStoreHours] = useState(true);

  const pushEnvironment = getPushEnvironment();
  const pushSupportedOnThisClient = NotificationService.isSupported();
  const pushSupportMessage = getPushSupportMessage(pushEnvironment);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Lade Benachrichtigungs-Einstellungen
      const currentSettings = await NotificationService.getSettings();
      setSettings(currentSettings);

      // Lade Subscriptions
      const currentSubscriptions = await NotificationService.getSubscriptions();
      setSubscriptions(currentSubscriptions);

      // Prüfe Subscription-Status
      const subscribed = await NotificationService.isSubscribed();
      setIsSubscribed(subscribed);

      // Prüfe Permission-Status
      setPermissionStatus(NotificationService.getPermissionStatus());

      // Lade Öffnungszeiten der aktuellen Filiale
      if (selectedStoreId) {
        const openingHours = await getOpeningHours(selectedStoreId);
        setStoreOpeningHours(openingHours);
        
        // Prüfe, ob die Einstellungen die Store-Zeiten verwenden sollen
        if (openingHours && currentSettings) {
          const storeStartTime = openingHours.openingHours?.[0]?.open || '07:00';
          const storeEndTime = openingHours.openingHours?.[openingHours.openingHours.length - 1]?.close || '20:00';
          
          // Wenn die aktuellen Zeiten den Store-Zeiten entsprechen, setze useStoreHours auf true
          if (currentSettings.preferences.schedule.start_time === storeStartTime && 
              currentSettings.preferences.schedule.end_time === storeEndTime) {
            setUseStoreHours(true);
          } else {
            setUseStoreHours(false);
          }
        }
      }
    } catch (error) {
      console.error('Fehler beim Laden der Benachrichtigungs-Daten:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStoreHoursToggle = async (enabled: boolean) => {
    if (!settings || !storeOpeningHours) return;

    setUseStoreHours(enabled);
    
    if (enabled && storeOpeningHours.openingHours?.length > 0) {
      // Verwende die Öffnungszeiten der Filiale
      const storeStartTime = storeOpeningHours.openingHours[0].open;
      const storeEndTime = storeOpeningHours.openingHours[storeOpeningHours.openingHours.length - 1].close;
      
      const newSettings = {
        ...settings,
        preferences: {
          ...settings.preferences,
          schedule: {
            ...settings.preferences.schedule,
            start_time: storeStartTime,
            end_time: storeEndTime,
            weekend_enabled: true // Immer aktiviert bei Store-Zeiten, da Store-Öffnungszeiten verwendet werden
          }
        }
      };
      
      await handleSettingsChange(newSettings);
    } else {
      // Bei manueller Einstellung, weekend_enabled auf false setzen als Standard
      const newSettings = {
        ...settings,
        preferences: {
          ...settings.preferences,
          schedule: {
            ...settings.preferences.schedule,
            weekend_enabled: false
          }
        }
      };
      
      await handleSettingsChange(newSettings);
    }
  };

  const getCurrentStoreName = () => {
    if (!selectedStoreId || !stores) return 'Unbekannter Markt';
    const store = stores.find(s => s.id === selectedStoreId);
    return store?.name || 'Unbekannter Markt';
  };

  const getRelevantNotificationTypes = () => {
    const { user } = useAuthStore.getState();
    const userRole = user?.role;
    
    // Rollenbasierte Benachrichtigungstypen
    const roleBasedTypes = {
      tasks: {
        show: userRole !== 'reinigungskraft' && userRole !== 'anonymous_guest', // Gaeste sehen nur systemweite Hinweise
        types: {
          new_assignment: { 
            label: 'Neue Aufgabe zugewiesen', 
            relevant: ['admin', 'filialleiter', 'vertretenderfilialleiter', 'user'] 
          },
          due_today: { 
            label: 'Aufgabe heute fällig', 
            relevant: ['admin', 'filialleiter', 'vertretenderfilialleiter', 'user'] 
          },
          overdue: { 
            label: 'Aufgabe überfällig', 
            relevant: ['admin', 'filialleiter', 'vertretenderfilialleiter', 'user'] 
          },
          completed: { 
            label: 'Aufgabe erledigt', 
            relevant: ['admin', 'filialleiter', 'vertretenderfilialleiter'] 
          }
        }
      },
      cleaning: {
        show: userRole !== 'anonymous_guest',
        types: {
          new_task: { 
            label: 'Neue Reinigungsaufgabe', 
            relevant: ['reinigungskraft', 'admin', 'filialleiter'] 
          },
          due_today: { 
            label: 'Reinigung heute fällig', 
            relevant: ['reinigungskraft', 'admin', 'filialleiter'] 
          },
          overdue: { 
            label: 'Reinigung überfällig', 
            relevant: ['reinigungskraft', 'admin', 'filialleiter'] 
          },
          approval_needed: { 
            label: 'Freigabe erforderlich', 
            relevant: ['admin', 'filialleiter', 'vertretenderfilialleiter'] 
          },
          approved: { 
            label: 'Freigabe erteilt', 
            relevant: ['reinigungskraft'] 
          }
        }
      },
      system: {
        show: true,
        types: {
          maintenance: { 
            label: 'Wartungsarbeiten', 
            relevant: ['admin'] 
          },
          updates: { 
            label: 'System-Updates', 
            relevant: ['admin'] 
          },
          emergency: { 
            label: 'Notfallmeldungen', 
            relevant: ['admin', 'filialleiter', 'vertretenderfilialleiter', 'user', 'reinigungskraft', 'anonymous_guest'] 
          }
        }
      }
    };

    return roleBasedTypes;
  };

  const handleSubscribeToggle = async () => {
    setLoading(true);
    try {
      if (isSubscribed) {
        await NotificationService.unsubscribe();
        setIsSubscribed(false);
      } else {
        const success = await NotificationService.subscribe();
        setIsSubscribed(success);
      }
      
      // Aktualisiere Subscriptions Liste
      const currentSubscriptions = await NotificationService.getSubscriptions();
      setSubscriptions(currentSubscriptions);
      setPermissionStatus(NotificationService.getPermissionStatus());
    } catch (error) {
      console.error('Fehler beim Ändern der Subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsChange = async (newSettings: Partial<INotificationSettings>) => {
    if (!settings) return;

    setLoading(true);
    try {
      const success = await NotificationService.updateSettings(newSettings);
      if (success) {
        setSettings({ ...settings, ...newSettings });
      }
    } catch (error) {
      console.error('Fehler beim Speichern der Einstellungen:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTestNotification = async () => {
    setLoading(true);
    try {
      await NotificationService.sendTestNotification('Test-Benachrichtigung aus den Einstellungen');
      alert('Test-Benachrichtigung wurde gesendet! Wechseln Sie zu einem anderen Tab um sie zu sehen.');
    } catch (error) {
      console.error('Fehler beim Senden der Test-Benachrichtigung:', error);
      alert('Fehler beim Test der Benachrichtigung. Prüfen Sie die Browser-Konsole für Details.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveDevice = async (subscriptionId: string) => {
    if (!confirm('Möchten Sie die Benachrichtigungen für dieses Gerät wirklich deaktivieren?')) {
      return;
    }

    setLoading(true);
    try {
      const success = await NotificationService.removeSubscription(subscriptionId);

      if (!success) {
        throw new Error('Fehler beim Entfernen des Geräts');
      }

      const currentSubscriptions = await NotificationService.getSubscriptions();
      setSubscriptions(currentSubscriptions);
      setIsSubscribed(currentSubscriptions.length > 0);
    } catch (error) {
      console.error('Fehler beim Entfernen des Geräts:', error);
      alert('Fehler beim Entfernen des Geräts. Versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = (category: string, key: string, value: boolean) => {
    if (!settings) return;

    const newPreferences = {
      ...settings.preferences,
      [category]: {
        ...settings.preferences[category as keyof typeof settings.preferences],
        [key]: value
      }
    };

    handleSettingsChange({ preferences: newPreferences });
  };

  const updateSchedule = (key: string, value: string | boolean) => {
    if (!settings) return;

    const newPreferences = {
      ...settings.preferences,
      schedule: {
        ...settings.preferences.schedule,
        [key]: value
      }
    };

    handleSettingsChange({ preferences: newPreferences });
  };

  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType) {
      case 'mobile':
        return <Smartphone className="h-4 w-4" />;
      case 'desktop':
        return <Monitor className="h-4 w-4" />;
      case 'tablet':
        return <Smartphone className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const getPermissionStatusText = () => {
    switch (permissionStatus) {
      case 'granted':
        return { text: 'Berechtigung erteilt', color: 'text-green-600' };
      case 'denied':
        return { text: 'Berechtigung verweigert', color: 'text-red-600' };
      default:
        return { text: 'Berechtigung ausstehend', color: 'text-yellow-600' };
    }
  };

  if (!isOpen) return null;

  return (
    <Portal label="NotificationSettingsModal">
      <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-start justify-center p-2 pt-6 sm:p-4 sm:pt-12">
        <div className="relative my-4 sm:my-8 w-full max-w-6xl max-h-[calc(var(--app-window-height)-1rem)] sm:max-h-[calc(var(--app-window-height)*0.9)] bg-white rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 sm:px-6 sm:py-4 z-10">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-xl">
                <Bell className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  Benachrichtigungs-Einstellungen
                </h3>
                <p className="text-sm text-gray-500">
                  Verwalten Sie Ihre Push-Benachrichtigungen und Zeitpläne
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

          <div className="overflow-y-auto flex-1 min-h-0 px-6 py-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600 font-medium">Lade Einstellungen...</p>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Hinweis für Tablet-Clients */}
              {!pushSupportedOnThisClient && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
                    <div>
                      <h4 className="text-yellow-800 font-medium">Push auf diesem Geraet nicht verfuegbar</h4>
                      <p className="text-yellow-700 text-sm mt-1">
                        {pushSupportMessage}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Status Overview */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-xl shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <Bell className="h-5 w-5 mr-2 text-blue-600" />
                  Status Übersicht
                </h4>
                
                {/* Status Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center space-x-3">
                      {isSubscribed ? (
                        <div className="p-2 bg-green-100 rounded-full">
                          <Bell className="h-5 w-5 text-green-600" />
                        </div>
                      ) : (
                        <div className="p-2 bg-gray-100 rounded-full">
                          <BellOff className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-gray-900">Abonnement</p>
                        <p className={`text-sm ${isSubscribed ? 'text-green-600' : 'text-gray-500'}`}>
                          {isSubscribed ? 'Aktiv' : 'Inaktiv'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-full ${
                        permissionStatus === 'granted' ? 'bg-green-100' :
                        permissionStatus === 'denied' ? 'bg-red-100' : 'bg-yellow-100'
                      }`}>
                        <div className={`h-3 w-3 rounded-full ${getPermissionStatusText().color.replace('text-', 'bg-')}`}></div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Berechtigung</p>
                        <p className={`text-sm ${getPermissionStatusText().color}`}>
                          {getPermissionStatusText().text.replace('Berechtigung ', '')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-blue-100 rounded-full">
                        <Settings className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Geräte</p>
                        <p className="text-sm text-gray-700">
                          {subscriptions.length} registriert
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleSubscribeToggle}
                    disabled={loading || !pushSupportedOnThisClient}
                    className={`flex-1 sm:flex-none px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2 ${
                      isSubscribed
                        ? 'bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                        : 'bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                    } disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none`}
                  >
                    {isSubscribed ? (
                      <BellOff className="h-4 w-4" />
                    ) : (
                      <Bell className="h-4 w-4" />
                    )}
                    <span>
                      {!pushSupportedOnThisClient
                        ? 'Nur auf Smartphone-Browsern/PWAs verfuegbar'
                        : isSubscribed ? 'Deabonnieren' : 'Benachrichtigungen aktivieren'
                      }
                    </span>
                  </button>

                  {isSubscribed && pushSupportedOnThisClient && (
                    <button
                      onClick={handleTestNotification}
                      disabled={loading}
                      className="flex-1 sm:flex-none px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:transform-none disabled:shadow-none flex items-center justify-center space-x-2"
                    >
                      <TestTube className="h-4 w-4" />
                      <span>Test-Benachrichtigung</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Active Subscriptions */}
              {subscriptions.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                    <Monitor className="h-5 w-5 mr-2 text-blue-600" />
                    Registrierte Geräte ({subscriptions.length})
                  </h4>
                  
                  <div className="space-y-3">
                    {subscriptions.map((sub) => (
                      <div key={sub.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
                        <div className="flex items-center space-x-4">
                          <div className="p-2 bg-white rounded-lg shadow-sm">
                            {getDeviceIcon(sub.device_type)}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 capitalize">
                              {sub.device_type === 'mobile' ? 'Mobilgerät' : 
                               sub.device_type === 'desktop' ? 'Desktop' : 
                               sub.device_type}
                            </p>
                            <p className="text-xs text-gray-500">
                              Zuletzt verwendet: {new Date(sub.last_used_at).toLocaleDateString('de-DE', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveDevice(sub.id)}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200 transform hover:scale-110"
                          title="Gerät entfernen"
                          disabled={loading}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <div className="p-1 bg-blue-100 rounded-full mt-0.5">
                        <AlertTriangle className="h-3 w-3 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-blue-800 font-medium">Geräte-Verwaltung</p>
                        <p className="text-xs text-blue-700 mt-1">
                          Sie können einzelne Geräte entfernen, ohne alle Benachrichtigungen zu deaktivieren. 
                          Dies ist nützlich, wenn Sie alte oder nicht mehr verwendete Geräte aus der Liste entfernen möchten.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Notification Settings */}
              {settings && (
                <div className="space-y-8">
                  {/* Global Enable/Disable */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-900 flex items-center">
                          <Settings className="h-5 w-5 mr-2 text-blue-600" />
                          Benachrichtigungen
                        </h4>
                        <p className="text-sm text-gray-500 mt-1">
                          Globale Aktivierung aller Benachrichtigungstypen
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.globalEnabled}
                          onChange={(e) => handleSettingsChange({ globalEnabled: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600 shadow-inner"></div>
                      </label>
                    </div>
                  </div>

                  {/* Task Notifications */}
                  {getRelevantNotificationTypes().tasks.show && (
                    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                        <span className="text-xl mr-2">📋</span>
                        Aufgaben-Benachrichtigungen
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(settings.preferences.tasks).map(([key, value]) => {
                          const { user } = useAuthStore.getState();
                          const userRole = user?.role;
                          const notificationTypes = getRelevantNotificationTypes();
                          const typeInfo = notificationTypes.tasks.types[key as keyof typeof notificationTypes.tasks.types];
                          
                          // Nur anzeigen, wenn relevant für die Benutzerrolle
                          if (!typeInfo || !typeInfo.relevant.includes(userRole || '')) {
                            return null;
                          }
                          
                          return (
                            <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="text-sm text-gray-700 font-medium">
                                {typeInfo.label}
                              </span>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={value}
                                  onChange={(e) => updatePreference('tasks', key, e.target.checked)}
                                  className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Cleaning Notifications */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <span className="text-xl mr-2">🧽</span>
                      Reinigungs-Benachrichtigungen
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(settings.preferences.cleaning).map(([key, value]) => {
                        const { user } = useAuthStore.getState();
                        const userRole = user?.role;
                        const notificationTypes = getRelevantNotificationTypes();
                        const typeInfo = notificationTypes.cleaning.types[key as keyof typeof notificationTypes.cleaning.types];
                        
                        // Nur anzeigen, wenn relevant für die Benutzerrolle
                        if (!typeInfo || !typeInfo.relevant.includes(userRole || '')) {
                          return null;
                        }
                        
                        return (
                          <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm text-gray-700 font-medium">
                              {typeInfo.label}
                            </span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={value}
                                onChange={(e) => updatePreference('cleaning', key, e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* System Notifications */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <span className="text-xl mr-2">⚙️</span>
                      System-Benachrichtigungen
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(settings.preferences.system).map(([key, value]) => {
                        const { user } = useAuthStore.getState();
                        const userRole = user?.role;
                        const notificationTypes = getRelevantNotificationTypes();
                        const typeInfo = notificationTypes.system.types[key as keyof typeof notificationTypes.system.types];
                        
                        // Nur anzeigen, wenn relevant für die Benutzerrolle
                        if (!typeInfo || !typeInfo.relevant.includes(userRole || '')) {
                          return null;
                        }
                        
                        return (
                          <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm text-gray-700 font-medium">
                              {typeInfo.label}
                            </span>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={value}
                                onChange={(e) => updatePreference('system', key, e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Schedule Settings */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <Clock className="h-5 w-5 mr-2 text-blue-600" />
                      Zeitplan-Einstellungen
                    </h4>
                    
                    {/* Filial-Info und Store-Zeiten verwenden */}
                    <div className="space-y-6">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <Store className="h-5 w-5 text-blue-600 mr-2" />
                          <h5 className="text-sm font-semibold text-blue-900">
                            Aktueller Markt: {getCurrentStoreName()}
                          </h5>
                        </div>
                        {storeOpeningHours && (
                          <div className="text-sm text-blue-700 space-y-1">
                            <p className="font-medium">
                              Öffnungszeiten: {storeOpeningHours.openingHours?.map((hours) => 
                                `${hours.open} - ${hours.close}`
                              ).join(', ') || 'Nicht verfügbar'}
                            </p>
                            <p className="flex items-center">
                              <span className={`inline-block w-2 h-2 rounded-full mr-2 ${storeOpeningHours.isOpen ? 'bg-green-500' : 'bg-red-500'}`}></span>
                              Status: {storeOpeningHours.isOpen ? 'Geöffnet' : 'Geschlossen'}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-sm font-semibold text-gray-900">Markt-Öffnungszeiten verwenden</span>
                            <p className="text-xs text-gray-600 mt-1">
                              Benachrichtigungen automatisch auf die Öffnungszeiten des Marktes abstimmen
                            </p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={useStoreHours}
                              onChange={(e) => handleStoreHoursToggle(e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600 shadow-inner"></div>
                          </label>
                        </div>
                      </div>

                      {/* Zeit-Eingabefelder */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            {useStoreHours ? 'Beginn (automatisch)' : 'Beginn (manuell)'}
                          </label>
                          <input
                            type="time"
                            value={settings?.preferences.schedule.start_time || '08:00'}
                            onChange={(e) => updateSchedule('start_time', e.target.value)}
                            disabled={useStoreHours}
                            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                              useStoreHours 
                                ? 'border-gray-300 bg-gray-100 text-gray-600 cursor-not-allowed' 
                                : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400'
                            }`}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            {useStoreHours ? 'Ende (automatisch)' : 'Ende (manuell)'}
                          </label>
                          <input
                            type="time"
                            value={settings?.preferences.schedule.end_time || '18:00'}
                            onChange={(e) => updateSchedule('end_time', e.target.value)}
                            disabled={useStoreHours}
                            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                              useStoreHours 
                                ? 'border-gray-300 bg-gray-100 text-gray-600 cursor-not-allowed' 
                                : 'border-gray-300 bg-white text-gray-900 hover:border-gray-400'
                            }`}
                          />
                        </div>
                      </div>

                      {/* Info-Boxen */}
                      <div className="space-y-3">
                        {useStoreHours ? (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex items-start space-x-3">
                              <div className="p-1 bg-green-100 rounded-full mt-1">
                                <Clock className="h-4 w-4 text-green-600" />
                              </div>
                              <div>
                                <h6 className="text-sm font-semibold text-green-900">
                                  Automatische Zeitplanung aktiviert
                                </h6>
                                <p className="text-xs text-green-700 mt-1 leading-relaxed">
                                  Benachrichtigungen werden nur während der tatsächlichen Öffnungszeiten des Marktes gesendet, 
                                  einschließlich Samstag wenn geöffnet. Sonn- und Feiertage werden automatisch berücksichtigt.
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-start space-x-3">
                              <div className="p-1 bg-yellow-100 rounded-full mt-1">
                                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                              </div>
                              <div>
                                <h6 className="text-sm font-semibold text-yellow-900">
                                  Manuelle Zeitplanung aktiv
                                </h6>
                                <p className="text-xs text-yellow-700 mt-1 leading-relaxed">
                                  Sie verwenden feste Zeiten. Benachrichtigungen werden täglich innerhalb der eingestellten Zeiten gesendet, 
                                  unabhängig von den Markt-Öffnungszeiten.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Tägliche Zusammenfassung */}
                      <div className="border-t pt-6">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <span className="text-sm font-semibold text-gray-900">Tägliche Zusammenfassung</span>
                            <p className="text-xs text-gray-600 mt-1">
                              Erhalten Sie eine tägliche Übersicht aller Aktivitäten
                            </p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={settings?.preferences.schedule.daily_summary || false}
                              onChange={(e) => updateSchedule('daily_summary', e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                        </div>

                        {settings?.preferences.schedule.daily_summary && (
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Zeit für tägliche Zusammenfassung
                            </label>
                            <input
                              type="time"
                              value={settings?.preferences.schedule.daily_summary_time || '18:00'}
                              onChange={(e) => updateSchedule('daily_summary_time', e.target.value)}
                              className="w-full md:w-48 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 hover:border-gray-400 transition-colors"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default NotificationSettings;
